import { useCallback, useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { CtaNote, Reveal, WhatsAppCta } from "@/components/brand";
import BrowserShot from "@/components/BrowserShot";
import CasePreview from "@/components/sections/CasePreview";
import { track } from "@/lib/analytics";
import { CTA_NOTE, projects } from "@/lib/offer";

const PAGE_TITLE = "Projetos | Ambitie";

export default function Projects() {
  const [open, setOpen] = useState(null);
  const close = useCallback(() => setOpen(null), []);
  const show = (item) => {
    track("case_preview_open", { case: item.name, page: "projetos" });
    setOpen(item);
  };

  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <section data-testid="projects-page" className="pb-20 pt-32 md:pb-28 md:pt-40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <p className="text-sm font-medium text-primary">Projetos</p>
          <h1 className="mt-2 text-4xl font-bold tracking-[-0.03em] text-foreground md:text-6xl">
            O que já construímos
          </h1>
          <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
            Sites e sistemas feitos sob medida, dos clientes no ar hoje aos conceitos que mostram
            como trabalhamos em cada segmento.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-x-6 gap-y-10 md:grid-cols-2">
          {projects.map((item, i) => (
            <Reveal key={item.name} delay={0.06 * (i % 2)}>
              <button
                type="button"
                onClick={() => show(item)}
                data-testid={`project-card-${i}`}
                className="group flex w-full flex-col text-left"
              >
                <BrowserShot
                  src={item.fullImage}
                  alt={`Site ${item.name}`}
                  url={item.url}
                  motion="hover"
                  className="transition-colors group-hover:border-primary/40"
                />
                <div className="mt-4 flex items-start justify-between gap-4 px-1">
                  <div>
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-primary">
                      {item.type}
                      {item.demo && (
                        <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                          Projeto demonstrativo
                        </span>
                      )}
                    </p>
                    <h2 className="mt-1 text-xl font-semibold tracking-[-0.01em] text-foreground md:text-2xl">
                      {item.name}
                    </h2>
                    {item.result && (
                      <p className="mt-1 text-sm font-semibold text-foreground">{item.result}</p>
                    )}
                    <p className="mt-2 max-w-md leading-relaxed text-muted-foreground">{item.summary}</p>
                  </div>
                  <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-[color,transform] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary" />
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-20 flex flex-col items-start gap-3 border-t border-border pt-10 md:flex-row md:items-center md:justify-between">
          <h2 className="text-2xl font-bold tracking-[-0.02em] text-foreground md:text-3xl">
            O próximo pode ser o seu.
          </h2>
          <div className="flex flex-col gap-2 md:items-end">
            <WhatsAppCta location="projects-page" data-testid="projects-cta" />
            <CtaNote>{CTA_NOTE}</CtaNote>
          </div>
        </Reveal>
      </div>

      <CasePreview item={open} onClose={close} />
    </section>
  );
}
